import React from 'react'
import { Search, X } from 'lucide-react'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface ClientFiltersProps {
  onSearchChange: (search: string) => void
  onStatusChange: (status: string) => void
  searchValue: string
  statusValue: string
  resultCount: number
  totalCount: number
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ClientFilters - Search and filter controls for client list
 *
 * Features:
 * - Search input with icon and clear button
 * - Status filter dropdown (All, Active, Inactive, Pending)
 * - Clear filters button (shown when filters are active)
 * - Results count display
 * - Responsive layout (stacks on mobile)
 * - Monumenta design system styling
 *
 * @param onSearchChange - Callback when search input changes
 * @param onStatusChange - Callback when status filter changes
 * @param searchValue - Current search value
 * @param statusValue - Current status filter value
 * @param resultCount - Number of filtered results
 * @param totalCount - Total number of clients
 */
export function ClientFilters({
  onSearchChange,
  onStatusChange,
  searchValue,
  statusValue,
  resultCount,
  totalCount,
}: ClientFiltersProps) {
  // Check if any filters are active
  const hasActiveFilters = searchValue !== '' || statusValue !== 'all'

  // Handle clearing all filters
  const handleClearFilters = () => {
    onSearchChange('')
    onStatusChange('all')
  }

  return (
    <div className="card mb-6">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-brand-gray" />
          <input
            type="text"
            placeholder="Search clients by name..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="form-input pl-10 pr-10 w-full"
            aria-label="Search clients"
          />
          {searchValue && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-80 transition-opacity"
              aria-label="Clear search"
            >
              <X className="h-5 w-5 text-brand-gray hover:text-white" />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <div className="sm:w-48">
          <select
            value={statusValue}
            onChange={(e) => onStatusChange(e.target.value)}
            className="form-select w-full"
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="btn-ghost sm:w-auto flex items-center gap-2 justify-center"
            aria-label="Clear all filters"
          >
            <X size={18} />
            <span>Clear Filters</span>
          </button>
        )}
      </div>

      {/* Results Count */}
      <div className="mt-3 text-sm text-brand-gray-light font-body">
        Showing {resultCount} of {totalCount} client{totalCount !== 1 ? 's' : ''}
      </div>
    </div>
  )
}

export default ClientFilters
